import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getDashboardStats, 
  getDashboardCharts, 
  getPendingActions 
} from '../../services/api';

function Dashboard({ admin, onLogout }) {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [pendingActions, setPendingActions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7d');

  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsData, chartsData, pendingData] = await Promise.all([
        getDashboardStats(),
        getDashboardCharts(),
        getPendingActions()
      ]);

      setStats(statsData.data);
      setCharts(chartsData.data);
      setPendingActions(pendingData.data);
    } catch (err) {
      console.error('Dashboard data error:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'view_pending_sellers':
        navigate('/admin/sellers?status=pending');
        break;
      case 'view_pending_listings':
        navigate('/admin/listings?moderation_status=pending');
        break;
      case 'view_disputes':
        navigate('/admin/orders?disputed=true');
        break;
      default:
        break;
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center py-6 space-y-4 md:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Welcome back, <span className="text-[#ff660e]">{admin?.first_name}!</span> 
                <span className="ml-2">👋</span>
              </h1>
              <p className="text-gray-400 mt-1">Here's what's happening with your marketplace today.</p>
            </div>
            <div className="flex items-center space-x-4">
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              <button 
                onClick={fetchDashboardData}
                className="bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg px-4 py-2 text-white transition-all duration-200 flex items-center space-x-2 hover:scale-[1.02]"
              >
                <span>🔄</span>
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-center justify-between backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button 
              onClick={fetchDashboardData}
              className="text-red-400 hover:text-red-300 font-medium transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Revenue Card */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:shadow-xl hover:border-[#ff660e]/50 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Total Revenue</p>
                <p className="text-3xl font-bold text-white mt-2 group-hover:text-[#ff660e] transition-colors">
                  {formatCurrency(stats?.revenue?.total_revenue)}
                </p>
                <p className="text-sm text-green-400 font-medium mt-1 flex items-center">
                  <span className="mr-1">↑</span>
                  {formatCurrency(stats?.revenue?.revenue_today || 0)} today
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-300 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-2xl text-white">💰</span>
              </div>
            </div>
          </div>

          {/* Users Card */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:shadow-xl hover:border-[#ff660e]/50 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Total Users</p>
                <p className="text-3xl font-bold text-white mt-2 group-hover:text-[#ff660e] transition-colors">
                  {formatNumber(stats?.users?.total_users)}
                </p>
                <p className="text-sm text-green-400 font-medium mt-1">
                  +{formatNumber(stats?.users?.new_users_7d || 0)} this week
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-2xl text-white">👥</span>
              </div>
            </div>
          </div>

          {/* Listings Card */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:shadow-xl hover:border-[#ff660e]/50 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Active Listings</p>
                <p className="text-3xl font-bold text-white mt-2 group-hover:text-[#ff660e] transition-colors">
                  {formatNumber(stats?.listings?.active_listings)}
                </p>
                <p className="text-sm text-blue-400 font-medium mt-1">
                  {formatNumber(stats?.listings?.new_listings_7d || 0)} new this week
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-2xl text-white">📦</span>
              </div>
            </div>
          </div>

          {/* Orders Card */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:shadow-xl hover:border-[#ff660e]/50 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Total Orders</p>
                <p className="text-3xl font-bold text-white mt-2 group-hover:text-[#ff660e] transition-colors">
                  {formatNumber(stats?.orders?.total_orders)}
                </p>
                <p className="text-sm text-purple-400 font-medium mt-1">
                  +{formatNumber(stats?.orders?.new_orders_7d || 0)} this week
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-2xl text-white">🛒</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-8">
            {/* Pending Actions */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <span className="mr-3 text-[#ff660e] text-xl">🚨</span>
                  Pending Actions
                </h3>
                <span className="bg-[#ff660e] text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                  {pendingActions ? 
                    (pendingActions.pending_sellers?.length || 0) + 
                    (pendingActions.pending_listings?.length || 0) + 
                    (pendingActions.pending_disputes?.length || 0) 
                    : 0}
                </span>
              </div>
              
              <div className="space-y-4">
                {/* Pending Sellers */}
                <div className="flex items-center justify-between p-4 bg-red-900/20 border-l-4 border-[#ff660e] rounded-r-lg hover:bg-red-900/30 transition-colors">
                  <div>
                    <h4 className="font-semibold text-white">Seller Approvals</h4>
                    <p className="text-sm text-gray-400">
                      {pendingActions?.pending_sellers?.length || 0} waiting for review
                    </p>
                  </div>
                  <button 
                    onClick={() => handleQuickAction('view_pending_sellers')}
                    className="bg-[#ff660e] hover:bg-[#ff5500] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Review
                  </button>
                </div>

                {/* Pending Listings */}
                <div className="flex items-center justify-between p-4 bg-yellow-900/20 border-l-4 border-[#ff660e] rounded-r-lg hover:bg-yellow-900/30 transition-colors">
                  <div>
                    <h4 className="font-semibold text-white">Listing Moderation</h4>
                    <p className="text-sm text-gray-400">
                      {pendingActions?.pending_listings?.length || 0} need approval
                    </p>
                  </div>
                  <button 
                    onClick={() => handleQuickAction('view_pending_listings')}
                    className="bg-[#ff660e] hover:bg-[#ff5500] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Review
                  </button>
                </div>

                {/* Pending Disputes */}
                <div className="flex items-center justify-between p-4 bg-blue-900/20 border-l-4 border-[#ff660e] rounded-r-lg hover:bg-blue-900/30 transition-colors">
                  <div>
                    <h4 className="font-semibold text-white">Order Disputes</h4>
                    <p className="text-sm text-gray-400">
                      {pendingActions?.pending_disputes?.length || 0} need resolution
                    </p>
                  </div>
                  <button 
                    onClick={() => handleQuickAction('view_disputes')}
                    className="bg-[#ff660e] hover:bg-[#ff5500] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Resolve
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-3 text-[#ff660e] text-xl">📋</span>
                Recent Activity
              </h3>
              
              <div className="space-y-4">
                {stats?.recent_activity?.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center space-x-4 py-3 border-b border-gray-700 last:border-0 hover:bg-gray-750 rounded-lg px-2 transition-colors">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#ff660e] to-orange-300 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {activity.first_name?.[0]}{activity.last_name?.[0]}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-200">
                        <span className="font-semibold text-white">{activity.first_name} {activity.last_name}</span>
                        {' '}<span className="text-gray-400">{activity.description}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.created_at).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                
                {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
                  <div className="text-center text-gray-500 py-8 border border-gray-700 rounded-lg">
                    <span className="text-3xl mb-2 block">📭</span>
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-8">
            {/* Platform Health */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-3 text-[#ff660e] text-xl">📊</span>
                Platform Health
              </h3>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-300">User Growth</span>
                    <span className="text-sm font-semibold text-green-400">
                      {stats?.users?.growth_rate_7d ? `${stats.users.growth_rate_7d}%` : '0%'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div 
                      className="bg-gradient-to-r from-[#ff660e] to-orange-400 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(stats?.users?.growth_rate_7d || 0, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-300">Order Completion</span>
                    <span className="text-sm font-semibold text-green-400">
                      {stats?.orders?.completed_orders ? 
                        `${((stats.orders.completed_orders / stats.orders.total_orders) * 100).toFixed(1)}%` 
                        : '0%'
                      }
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div 
                      className="bg-gradient-to-r from-[#ff660e] to-orange-400 h-2.5 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(
                          stats?.orders?.completed_orders && stats.orders.total_orders ? 
                          (stats.orders.completed_orders / stats.orders.total_orders) * 100 : 0, 
                          100
                        )}%` 
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-300">Seller Approval Rate</span>
                    <span className="text-sm font-semibold text-blue-400">
                      {stats?.sellers?.approved_sellers ? 
                        `${((stats.sellers.approved_sellers / stats.sellers.total_sellers) * 100).toFixed(1)}%` 
                        : '0%'
                      }
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div 
                      className="bg-gradient-to-r from-[#ff660e] to-orange-400 h-2.5 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(
                          stats?.sellers?.approved_sellers && stats.sellers.total_sellers ? 
                          (stats.sellers.approved_sellers / stats.sellers.total_sellers) * 100 : 0, 
                          100
                        )}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-3 text-[#ff660e] text-xl">⚡</span>
                Quick Actions
              </h3>
              
              <div className="grid grid-cols-1 gap-3">
                <button 
                  onClick={() => navigate('/admin/sellers?status=pending')}
                  className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] text-left flex items-center shadow-lg"
                >
                  <span className="mr-3">👥</span>
                  Review Sellers
                </button>
                <button 
                  onClick={() => navigate('/admin/listings?moderation_status=pending')}
                  className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] text-left flex items-center shadow-lg"
                >
                  <span className="mr-3">📦</span>
                  Moderate Listings
                </button>
                <button 
                  onClick={() => navigate('/admin/orders')}
                  className="w-full bg-gray-700 border border-gray-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-600 hover:border-[#ff660e] transition-all duration-200 text-left flex items-center"
                >
                  <span className="mr-3">🛒</span>
                  View Orders
                </button>
                <button 
                  onClick={() => navigate('/admin/finance/transactions')}
                  className="w-full bg-gray-700 border border-gray-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-600 hover:border-[#ff660e] transition-all duration-200 text-left flex items-center"
                >
                  <span className="mr-3">💰</span>
                  Financial Reports
                </button>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-3 text-[#ff660e] text-xl">🖥️</span>
                System Status
              </h3>
              
              <div className="space-y-4">
                {['API Server', 'Database', 'Email Service', 'Payment Gateway'].map((service) => (
                  <div key={service} className="flex items-center justify-between p-3 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-gray-300">{service}</span>
                    </div>
                    <span className="text-xs px-2 py-1 bg-green-900/30 text-green-400 rounded-full">Operational</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;